import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { HBox, VBox } from "@ui/box/Box";
import { TButtonVariant } from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { SpinnerProgress } from "@ui/spinner/SpinnerProgress";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import Signal from "badmfck-signal";
import { useEffect, useState } from "react";
import { IconDocument } from "src/assets/icon_document";
import { GD } from "src/GD";
import { CommonDispatcher } from "src/services/Common";
import { handleDownload } from "src/utils/tools";
import { safeJsonParseArray } from "src/utils/json";
import { Avatar } from "src/view/Avatar";
import { FormParser } from "src/view/FormParser";
import { S_CONFIRM } from "src/view/popup/general/ConfirmDialog";

export const S_WEBSITE_REQUEST_POPUP = new Signal<{ clientID: number; merchantID: number; onSuccess: () => void } | null>("S_WEBSITE_REQUEST_POPUP");

//!TODO: component for any form?
// Да
export const WebsiteRequestPopup = () => {
	useLocale();
	const requestData = S_WEBSITE_REQUEST_POPUP.use();
	const [_busy, setBusy] = useState<boolean>(false);
	const [state, setState] = useState<"small" | "full">("small");

	if (!requestData) return null;

	const closePopup = () => {
		setBusy(false);
		S_WEBSITE_REQUEST_POPUP.invoke(null);
	};

	const buttons: {
		title: string;
		onClick?: (data?: any) => void;
		sx?: RecursiveCSS;
		variant?: TButtonVariant;
	}[] = [
		{
			title: msg("comments"),
			variant: "secondarySmall",
			onClick: () => {
				if (state === "small") {
					setState("full");
				} else {
					setState("small");
				}
			},
		},
	];

	const contentWidth = "700px";

	let sx: RecursiveCSS = {};
	if (state === "small") {
		sx = { maxWidth: contentWidth, minWidth: "420px", width: "99vw", minHeight: "90vh", maxHeight: "90vh" };
	} else {
		sx = { maxWidth: "1100px", minWidth: "420px", width: "99vw", minHeight: "90vh", maxHeight: "90vh" };
	}

	sx = {
		...sx,
		transition: "all .2s",
	};

	return (
		<Panel sx={sx} contentPadding="20px" decorator="absolute" label={msg("websiteRequest")} onClose={closePopup}>
			<WebsiteRequestForm
				buttons={buttons}
				setBusy={setBusy}
				clientID={requestData.clientID}
				merchantID={requestData.merchantID}
				state={state}
				onComplete={close => {
					if (close) {
						closePopup();
					}
					requestData.onSuccess && requestData.onSuccess();
				}}
				allowEdit
			/>
		</Panel>
	);
};

export const WebsiteRequestForm = ({
	setBusy,
	clientID,
	merchantID,
	state,
	onComplete,
	buttons,
	allowEdit = false,
	appearance = "classic",
}: {
	setBusy: (busy: boolean) => void;
	clientID: number;
	merchantID: number;
	state?: "small" | "full";
	onComplete?: (close?: boolean) => void;
	buttons?: {
		title: string;
		onClick?: (data?: any) => void;
		sx?: RecursiveCSS;
		variant?: TButtonVariant;
	}[];
	allowEdit?: boolean | null;
	/** "modern" for a form placed on a tinted panel that scrolls by itself. */
	appearance?: "classic" | "modern";
}) => {
	useLocale();
	!state && (state = "small");

	const [status, setStatus] = useState<"submitted" | "created" | "aml_review" | "rejected" | "board_review" | "active" | "blocked" | "deleted" | undefined>();

	const innerButtons = buttons ? buttons : [];

	if (status === "submitted") {
		innerButtons.push({
			title: msg("sendToReview"),

			onClick: async () => {
				//!TODO: type;

				setBusy(true);
				const response = await GD.REQ_API.request({
					method: "clients/group/review",
					data: {
						client_id: clientID,
						group_id: merchantID,
					},
				});
				if (response && response.data) {
					if (onComplete) {
						onComplete(true);
					}

					setStatus("aml_review");
				}
				setBusy(false);
			},
		});
	}

	return (
		<HBox sx={{ flexGrow: 1, gap: "20px", alignItems: "stretch" }}>
			<VBox sx={{ flexGrow: 1 }}>
				<FormParser
					previewMode={!allowEdit}
					scrollable={appearance !== "modern"}
					appearance={appearance}
					size={appearance === "modern" ? "small" : undefined}
					sx={appearance === "modern" ? { padding: 0, borderRadius: 0, backgroundColor: "transparent", maxWidth: "none" } : undefined}
					key="websites_request"
					onSubmit={async (data: any) => {
						return new Promise((resolve, _reject) => {
							S_CONFIRM.invoke({
								title: msg("areYouSureYouWantToSaveChanges"),
								message: msg("currentWebsiteWillBeDeactivatedUntilChangessAreApproved"),
								onResult: async val => {
									if (val === "reject") {
										return;
									}
									setBusy(true);
									const response = await GD.REQ_API.request({
										method: "clients/group/add",
										data: data,
									});
									if (response && response.data && response.data.data) {
										setStatus(response.data.status);
									}
									setBusy(false);
									resolve(response);
								},
								confirm: msg("yesSaveChanges"),
								reject: msg("noKeepItAsIs"),
							});
						});
					}}
					onComplete={() => {
						if (onComplete) onComplete();
					}}
					onRequest={async () => {
						setBusy(true);
						const response = await GD.REQ_API.request({
							method: "clients/group/get",
							data: {
								client_id: clientID,
								group_id: merchantID,
							},
						});
						if (response && response.data) {
							setStatus(response.data.status);
						}
						setBusy(false);
						return response;
					}}
				/>
			</VBox>

			{state === "full" && <CommentsPanel sectionType={0} targetType="website_request" targetId={merchantID} />}
		</HBox>
	);
};

export interface ICommentsPanelProps {
	targetType: "client" | "clients" | "website_request";
	sectionType: number;
	targetId?: number;
	limit?: number;
}

const CommentsPanel = ({ targetType, sectionType, targetId, limit }: ICommentsPanelProps) => {
	useLocale();
	const [busy, setBusy] = useState<boolean>();

	const [comments, setComments] = useState<IComment[]>();
	const [error, setError] = useState<IError>();

	useEffect(() => {
		if (targetId) {
			loadComments();
		}
	}, [targetType, sectionType, targetId]);

	const loadComments = async () => {
		setBusy(true);
		setComments(undefined);
		setError(undefined);

		const req = await CommonDispatcher.REQ_COMMENTS.request({
			sectionType: sectionType,
			targetType: targetType,
			targetId: targetId,
			limit: limit,
		});

		if (req?.data) {
			setComments(req.data.slice(0, Math.min(req.data.length, 5)));
		} else {
			setError(req?.error);
		}
		setBusy(false);
	};

	const handleDownloadFile = (comment: IComment) => {
		GD.REQ_FILE_DOWNLOAD.request({
			target_id: comment.target_id,
			target_type: comment.target_type,
			id: comment.id,
		}).then(res => {
			//!TODO: remove any;
			handleDownload({
				...comment,
				res: res.data,
			} as any);
		});
	};

	return (
		<VBox sx={{ flexGrow: "1", backgroundColor: "#d3d3d3" }}>
			<VBox sx={{ padding: "20px" }}>
				<SpinnerProgress busy={busy ?? false} />
				{error && <Error error={error} />}

				{!busy && comments && (
					<VBox gap="16px">
						{comments.map((c, index) => (
							<VBox key={index}>
								<HBox
									sx={{
										alignItems: "center",
										gap: "4px",
									}}>
									<Avatar size="19px" />
									<Typo color="#0a1942" variant="caption" bold sx={{ wordWrap: "break-word" }}>
										{c.user_name ?? "user_" + c.user_id}
									</Typo>
									<Spacer grow />
									<Typo align="right" color="#757f9b" variant="small" sx={{ wordWrap: "break-word" }}>
										<DateTimeTooltip value={c.created_at}>{formatDisplayDateTime(c.created_at)}</DateTimeTooltip>
									</Typo>
								</HBox>
								<Spacer size="6px" />
								<VBox
									key={c.id}
									sx={{
										backgroundColor: "#ffffff",
										borderRadius: "6px",
										padding: "12px",
										boxShadow: "0px 2px 2px rgba(0,0,0,.1)",
									}}>
									{c.text && (
										<Typo variant="text" sx={{ wordWrap: "break-word" }}>
											{c.text}
										</Typo>
									)}

									{c.text && c.attachment && <Spacer size="8px" />}

									{c.attachment && (
										<>
											{safeJsonParseArray<IAttachment>(c.attachment).map((a, index) => (
												<HBox
													gap="4px"
													key={index}
													onClick={() => handleDownloadFile(c)}
													sx={{
														alignItems: "center",
														// borderBottom: "1px solid rgba(0,0,0,.1)",
														borderRadius: "6px",
														padding: "6px 8px",
														overflow: "hidden",
														cursor: "pointer",
														transition: "background-color .2s",
														"&:hover": {
															backgroundColor: "rgba(0,0,0,.08)",
															transition: "background-color .2s",
														},
													}}>
													<IconDocument />
													<Typo
														sx={{
															whiteSpace: "nowrap",
															textOverflow: "ellipsis",
															color: "#1976d2",
															fontSize: ".8rem",
														}}>
														{a.name}
													</Typo>
												</HBox>
											))}
										</>
									)}
								</VBox>
							</VBox>
						))}
					</VBox>
				)}
			</VBox>

			<Typo align="center" variant="headMedium" sx={{ backgroundColor: "#c0c0c0", cursor: "pointer" }}>{msg("comments")}</Typo>
		</VBox>
	);
};
